
// Utility to generate the .cfg file content from the configuration data
export const generateConfigString = (configData, activeModules, options = { format: 'raw' }) => {
    let output = ''
    const isDisplay = options.format === 'display'

    // Header
    output += '// CONFIG-STRIKE - config-strike.com\n\n'

    const modules = activeModules || []
    const data = configData

    if (modules.includes('mouse')) {
        output += '// CONFIG MOUSE\n'
        output += `sensitivity "${data.mouse.sensitivity}"\n`
        output += `zoom_sensitivity_ratio "${data.mouse.zoom_sensitivity_ratio}"\n`
        output += `m_rawinput "${data.mouse.m_rawinput ? '1' : '0'}"\n`
        output += `m_yaw "${data.mouse.m_yaw}"\n`
        output += `m_pitch "${data.mouse.m_pitch}"\n\n`
        output += `// eDPI: ${Math.round(data.mouse.sensitivity * data.mouse.dpi)}\n\n`
    }

    if (modules.includes('crosshair')) {
        output += '// CONFIG CROSSHAIR\n'

        const ch = data.crosshair

        const write = (key, val) => `${key} "${val}"\n`
        const header = (title) => isDisplay ? `\n// [ ${title} ]\n` : ''

        // General
        output += header('GENERAL')
        output += write('cl_crosshairstyle', ch.cl_crosshairstyle)
        output += write('cl_crosshairsize', ch.cl_crosshairsize)
        output += write('cl_crosshairthickness', ch.cl_crosshairthickness)
        output += write('cl_crosshairgap', ch.cl_crosshairgap)
        output += write('cl_crosshairdot', ch.cl_crosshairdot ? '1' : '0')

        // Color
        output += header('COLOR')
        output += write('cl_crosshaircolor', '5')
        output += write('cl_crosshaircolor_r', ch.cl_crosshaircolor_r)
        output += write('cl_crosshaircolor_g', ch.cl_crosshaircolor_g)
        output += write('cl_crosshaircolor_b', ch.cl_crosshaircolor_b)
        output += write('cl_crosshairalpha', ch.cl_crosshairalpha)

        // Outline
        output += header('OUTLINE')
        output += write('cl_crosshair_drawoutline', ch.cl_crosshair_drawoutline ? '1' : '0')
        output += write('cl_crosshair_outlinethickness', ch.cl_crosshair_outlinethickness)

        // Misc CS2
        output += header('CS2_ENHANCEMENTS')
        output += write('cl_crosshair_recoil', ch.cl_crosshair_recoil ? '1' : '0')
        output += write('cl_crosshair_t', ch.cl_crosshair_t ? '1' : '0')
        output += write('cl_crosshair_friendly_warning', ch.cl_crosshair_friendly_warning)
        output += write('cl_crosshair_sniper_width', ch.cl_crosshair_sniper_width)
        output += '\n'

        // Crosshair presets + toggle bind
        if (ch.presets && ch.presets.length > 0) {
            const sanitize = (name) => name.replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_')

            output += '// CROSSHAIR PRESETS\n'

            // crosshair_1 siempre es la mira activa actual (default)
            const defaultCmds = [
                `cl_crosshairstyle "${ch.cl_crosshairstyle}"`,
                `cl_crosshairsize "${ch.cl_crosshairsize}"`,
                `cl_crosshairthickness "${ch.cl_crosshairthickness}"`,
                `cl_crosshairgap "${ch.cl_crosshairgap}"`,
                `cl_crosshairdot "${ch.cl_crosshairdot ? '1' : '0'}"`,
                `cl_crosshaircolor "5"`,
                `cl_crosshaircolor_r "${ch.cl_crosshaircolor_r}"`,
                `cl_crosshaircolor_g "${ch.cl_crosshaircolor_g}"`,
                `cl_crosshaircolor_b "${ch.cl_crosshaircolor_b}"`,
                `cl_crosshairalpha "${ch.cl_crosshairalpha}"`,
                `cl_crosshair_drawoutline "${ch.cl_crosshair_drawoutline ? '1' : '0'}"`,
                `cl_crosshair_outlinethickness "${ch.cl_crosshair_outlinethickness}"`,
                `cl_crosshair_recoil "${ch.cl_crosshair_recoil ? '1' : '0'}"`,
                `cl_crosshair_t "${ch.cl_crosshair_t ? '1' : '0'}"`,
                `cl_crosshair_sniper_width "${ch.cl_crosshair_sniper_width}"`,
            ].join('; ')
            output += `alias "crosshair_1" "${defaultCmds}"\n`

            // Presets del usuario: crosshair_2, crosshair_3, ... (o por nombre si tienen)
            const seen = new Set(['crosshair_1'])
            const aliases = ['crosshair_1']

            ch.presets.forEach((preset, i) => {
                const base = preset.name && preset.name.trim()
                    ? `crosshair_${sanitize(preset.name.trim())}`
                    : `crosshair_${i + 2}`
                const alias = seen.has(base) ? `crosshair_${i + 2}` : base
                seen.add(alias)
                aliases.push(alias)
                const cmds = preset.commands.replace(/[\n\r]+/g, ' ').trim()
                output += `alias "${alias}" "${cmds}"\n`
            })

            if (ch.bind_toggle_presets) {
                output += '\n// CROSSHAIR TOGGLE\n'
                aliases.forEach((alias, i) => {
                    const next = aliases[(i + 1) % aliases.length]
                    output += `alias "apply_${alias}" "${alias}; alias toggle_crosshair apply_${next}"\n`
                })
                output += `alias "toggle_crosshair" "apply_${aliases[0]}"\n`
                output += `bind "${ch.bind_toggle_presets}" "toggle_crosshair"\n`
            }

            output += '\n'
        }
    }

    if (modules.includes('graphics')) {
        output += '// CONFIG GRAPHICS & PERFORMANCE\n'
        const g = data.graphics
        output += `r_drawtracers_firstperson "${g.r_drawtracers_firstperson ? '1' : '0'}"\n`
        output += `r_dynamic "${g.r_dynamic ? '1' : '0'}"\n`
        output += `cl_allow_animated_avatars "${g.cl_allow_animated_avatars ? '1' : '0'}"\n`
        output += `engine_low_latency_sleep_after_client_tick "${g.engine_low_latency_sleep_after_client_tick ? '1' : '0'}"\n`
        output += `fps_max "${g.fps_max}"\n`
        output += `cl_ragdoll_workaround_threshold "${g.cl_ragdoll_workaround_threshold ? '1' : '0'}"\n\n`
    }

    if (modules.includes('video')) {
        output += '// VIDEO SETTINGS (INFO FOR video.txt)\n'
        const v = data.video
        output += `// Resolution: ${v.resolution_width}x${v.resolution_height}\n`
        output += `// Refresh Rate: ${v.refresh_rate}Hz\n`
        output += `// Fullscreen: ${v.fullscreen ? 'Yes' : 'No'}\n`
        output += `// Aspect Ratio: ${v.aspect_ratio}\n\n`
    }

    if (modules.includes('viewmodel')) {
        output += '// CONFIG VIEWMODEL\n'

        const vm = data.viewmodel
        output += `viewmodel_offset_x "${vm.viewmodel_offset_x}"\n`
        output += `viewmodel_offset_y "${vm.viewmodel_offset_y}"\n`
        output += `viewmodel_offset_z "${vm.viewmodel_offset_z}"\n`
        output += `viewmodel_fov "${vm.viewmodel_fov}"\n`
        output += `viewmodel_presetpos "0"\n`
        output += `cl_prefer_lefthanded "${vm.cl_prefer_lefthanded}"\n`
        output += '\n'
    }

    if (modules.includes('hud')) {
        output += '// CONFIG HUD & RADAR\n'
        const h = data.hud
        output += `cl_hud_color "${h.cl_hud_color}"\n`
        output += `hud_scaling "${h.hud_scaling}"\n`
        output += `cl_radar_scale "${h.cl_radar_scale}"\n`
        output += `cl_hud_radar_scale "${h.cl_hud_radar_scale}"\n`
        output += `cl_radar_always_centered "${h.cl_radar_always_centered ? '1' : '0'}"\n`
        output += `cl_radar_rotate "${h.cl_radar_rotate ? '1' : '0'}"\n`
        output += `cl_showloadout "${h.cl_showloadout ? '1' : '0'}"\n`
        output += `cl_radar_icon_scale_min "${h.cl_radar_icon_scale_min || 0.6}"\n`
        output += `cl_teammate_colors_show "${h.cl_teammate_colors_show ?? 1}"\n`
        output += `cl_radar_square_with_scoreboard "${h.cl_radar_square_with_scoreboard ? '1' : '0'}"\n`
        output += `cl_hud_radar_background_alpha "${h.cl_hud_radar_background_alpha ?? 0.6}"\n`
        output += '\n'

        const b = data.binds
        // Radar Big (Hold)
        if (b && b.radar_big_key) {
            // Min scale (0.25) to see map, Max HUD scale (1.3) to see it clear
            output += `alias "+radar_big" "cl_radar_scale 0.25; cl_hud_radar_scale 1.3"\n`
            output += `alias "-radar_big" "cl_radar_scale ${h.cl_radar_scale || 0.7}; cl_hud_radar_scale ${h.cl_hud_radar_scale || 1.0}"\n`
            output += `bind "${b.radar_big_key}" "+radar_big"\n`
        }
        output += '\n'
    }

    // Helper for binds
    const b = data.binds || {}

    // CONFIG BASIC BINDS
    if (modules.includes('binds_basic') || modules.includes('binds')) {
        output += '// CONFIG BASIC BINDS\n'

        const basicBindMap = {
            // Movement
            key_forward: '+forward', key_back: '+back', key_left: '+left', key_right: '+right',
            key_walk: '+sprint', key_duck: '+duck', key_jump: '+jump',

            // Combat Essentials
            key_attack: '+attack', key_attack2: '+attack2', key_reload: '+reload',
            key_use: '+use', key_drop: 'drop', key_inspect: '+lookatweapon',
            key_lastinv: 'lastinv', key_buymenu: 'buymenu',
            key_autobuy: 'autobuy', key_rebuy: 'rebuy',

            // Weapon Slots
            key_slot1: 'slot1', key_slot2: 'slot2', key_slot3: 'slot3',
            key_slot4: 'slot4', key_slot5: 'slot5',

            // Communication
            key_mic: '+voicerecord', key_chat: 'messagemode', key_chat_team: 'messagemode2',
            key_ping: 'player_ping',
            key_radio1: 'radio1', key_radio2: 'radio2', key_radio3: 'radio3',

            // UI & Misc
            key_score: '+showscores', key_console: 'toggleconsole',
            key_spray: '+spray_menu', key_team_equip: '+cl_show_team_equipment',
            key_show_loadout: 'show_loadout_toggle', key_team_menu: 'teammenu',
            key_screenshot: 'jpeg', key_quit_prompt: 'cs_quit_prompt',
            key_sell_back_all: 'sellbackall',
            key_inv_next: 'invnext', key_inv_prev: 'invprev',
            key_cancel_select: 'cancelselect',
            key_radial_radio: '+radialradio', key_radial_radio2: '+radialradio2',
            viewmodel_switchhands_bind: 'switchhands'
        }

        Object.entries(basicBindMap).forEach(([key, command]) => {
            if (b[key]) {
                output += `bind "${b[key]}" "${command}"\n`
            }
        })
        output += '\n'
    }

    // CONFIG SPECIAL BINDS
    if (modules.includes('binds_special') || modules.includes('binds')) {
        output += '// CONFIG SPECIAL BINDS\n'

        const grenadeBindMap = {
            grenade_flash: { slot: 'slot7', buy: 'buy flashbang' },
            grenade_smoke: { slot: 'slot8', buy: 'buy smokegrenade' },
            grenade_he: { slot: 'slot6', buy: 'buy hegrenade' },
            grenade_molotov: { slot: 'slot10', buy: 'buy molotov; buy incgrenade' },
            grenade_decoy: { slot: 'slot9', buy: 'buy decoy' }
        }

        const otherBindMap = {
            key_taser: 'slot11',
            key_healthshot: 'slot12',
            bind_mute_all: 'voice_modenable_toggle',
            bind_vol_up: 'incrementvar volume 0 1 0.1',
            bind_vol_down: 'incrementvar volume 0 1 -0.1',
            bind_voice_up: 'incrementvar snd_voipvolume 0 1 0.1',
            bind_voice_down: 'incrementvar snd_voipvolume 0 1 -0.1',
            bind_radar_toggle: 'toggle cl_radar_scale 0.35 1'
        }

        // Grenade binds with optional auto-buy
        Object.entries(grenadeBindMap).forEach(([key, commands]) => {
            if (b[key]) {
                if (b.grenade_binds_buy) {
                    output += `bind "${b[key]}" "${commands.slot}; ${commands.buy}"\n`
                } else {
                    // Without auto-buy: just slot
                    output += `bind "${b[key]}" "${commands.slot}"\n`
                }
            }
        })

        // Other special binds
        Object.entries(otherBindMap).forEach(([key, command]) => {
            if (b[key]) {
                output += `bind "${b[key]}" "${command}"\n`
            }
        })

        // Jumpthrow for CS2 (Subtick stable)
        if (b.jump_throw_key && b.jump_throw_key !== 'none') {
            output += `alias "+jt" "+jump;"\n`
            output += `alias "+ta" "-attack; -attack2"\n`
            output += `alias "-jt" "-jump"\n`
            output += `bind "${b.jump_throw_key}" "+jt;+ta"\n`
        }

        // Mousewheel Jump
        if (b.mwheel_jump === 'both') {
            output += `bind "mwheelup" "+jump"; bind "mwheeldown" "+jump"\n`
        } else if (b.mwheel_jump === 'up') {
            output += `bind "mwheelup" "+jump"\n`
        } else if (b.mwheel_jump === 'down') {
            output += `bind "mwheeldown" "+jump"\n`
        }

        // Clear Decals (Auto-Shift)
        if (b.clear_decals) {
            output += `bind "shift" "+sprint; cl_clear_decals"\n`
        }
        output += '\n'
    }

    // CONFIG CUSTOM BUY BINDS
    if (modules.includes('binds_custom') && b.custom_binds?.length > 0) {
        output += '\n// CONFIG CUSTOM BUY BINDS\n'
        b.custom_binds.forEach((cb, idx) => {
            if (cb.key && cb.items?.length > 0) {
                const aliasName = `custom_buy_${idx + 1}`
                const buyCommands = cb.items.map(itemId => `buy ${itemId}`).join('; ')
                output += `alias "${aliasName}" "${buyCommands}"\n`
                output += `bind "${cb.key}" "${aliasName}"\n`
            }
        })
        output += '\n'
    }

    // CONFIG RADIO WHEELS
    if ((modules.includes('radio') || modules.includes('binds')) && data.settings) {
        const radialKeys = Object.keys(data.settings).filter(k => k.startsWith('cl_radial_radio_tab_'))

        if (radialKeys.length > 0) {
            output += '\n// CONFIG RADIO WHEELS\n'

            // Group by tab index (0, 1, 2)
            for (let i = 0; i < 3; i++) {
                const tabKeys = radialKeys.filter(k => k.includes(`_tab_${i}_`)).sort()
                if (tabKeys.length > 0) {
                    // i+1 for user friendly numbering (Wheel 1, 2, 3)
                    output += `\n// --- Radio Wheel ${i + 1} ---\n`
                    tabKeys.forEach(key => {
                        output += `${key} ${data.settings[key]}\n`
                    })
                }
            }
            output += '\n'
        }
    }

    // Custom Scripts Section
    if (modules.includes('custom_scripts') && data.custom_scripts?.raw_content) {
        output += '// CUSTOM SCRIPTS & ALIASES\n'
        output += data.custom_scripts.raw_content
        if (!data.custom_scripts.raw_content.endsWith('\n')) {
            output += '\n'
        }
        output += '\n'
    }

    if (modules.includes('practice')) {
        output += '\n// --- PRACTICE SETTINGS ---\n'
        const p = data.practice

        output += `alias "clear_util" "ent_fire smokegrenade_projectile kill; ent_fire molotov_projectile kill; ent_fire flashbang_projectile kill; ent_fire hegrenade_projectile kill; ent_fire decoy_projectile kill; stopsound"\n`

        if (options.separatePractice) {
            output += 'alias "prac" "exec practice"\n'
        } else {
            // Create a "prac" alias to re-init everything with one command
            // const isDisplay = options.format === 'display' // Already defined
            const lineBreak = isDisplay ? '\n    ' : ' '

            let pracAlias = 'alias "prac" "'
            if (p.sv_cheats) pracAlias += `sv_cheats 1;${lineBreak}`
            if (p.infinite_resources) {
                pracAlias += `sv_infinite_ammo 1;${lineBreak}mp_maxmoney 64000;${lineBreak}mp_startmoney 64000;${lineBreak}mp_afterroundmoney 64000;${lineBreak}mp_buytime 9999;${lineBreak}mp_buy_anywhere 1;${lineBreak}`
            }
            if (p.grenade_utils) {
                pracAlias += `sv_showimpacts 1;${lineBreak}sv_grenade_trajectory_prac_pipreview 1;${lineBreak}sv_grenade_trajectory_prac_trailtime 8;${lineBreak}ammo_grenade_limit_total 6;${lineBreak}`
            }
            if (p.server_optimization) {
                pracAlias += `mp_limitteams 0;${lineBreak}mp_autoteambalance 0;${lineBreak}mp_roundtime 60;${lineBreak}mp_roundtime_defuse 60;${lineBreak}mp_freezetime 0;${lineBreak}mp_warmup_end;${lineBreak}mp_restartgame 1;${lineBreak}`
            }
            if (p.bot_freeze) pracAlias += `bot_stop 1;${lineBreak}`
            if (p.auto_heal) pracAlias += `sv_regeneration_force_on 1;${lineBreak}`
            if (p.radar_full) pracAlias += `mp_radar_showall 1;${lineBreak}`
            if (p.wallbang_info) pracAlias += `sv_showimpacts_penetration 1;${lineBreak}`
            if (p.auto_bhop) pracAlias += `sv_autobunnyhopping 1;${lineBreak}sv_enablebunnyhopping 1;${lineBreak}`

            // Remove trailing newline/space for clean closing
            if (isDisplay) {
                pracAlias = pracAlias.trimEnd()
            }

            pracAlias += ' echo PRACTICE MODE ENABLED;"\n'
            output += pracAlias
        }

        output += '\n'

        // Binds
        if (p.bind_prac_alias) output += `bind "${p.bind_prac_alias}" "prac"\n`
        if (p.bind_noclip) output += `bind "${p.bind_noclip}" "noclip"\n`
        if (p.bind_clear_util) output += `bind "${p.bind_clear_util}" "clear_util"\n`
        if (p.bind_toggle_impacts) output += `bind "${p.bind_toggle_impacts}" "toggle sv_showimpacts 0 1 2"\n`
        if (p.bind_rethrow) output += `bind "${p.bind_rethrow}" "sv_rethrow_last_grenade"\n`
        if (p.bind_bot_place) output += `bind "${p.bind_bot_place}" "bot_place"\n`
        if (p.bind_bot_add_t) output += `bind "${p.bind_bot_add_t}" "bot_add_t"\n`
        if (p.bind_bot_add_ct) output += `bind "${p.bind_bot_add_ct}" "bot_add_ct"\n`
        if (p.bind_bot_kick) output += `bind "${p.bind_bot_kick}" "bot_kick"\n`
        if (p.bind_timescale) output += `bind "${p.bind_timescale}" "toggle host_timescale 1 10"\n`
        if (p.bind_restart_round) output += `bind "${p.bind_restart_round}" "mp_restartgame 1"\n`

        // Checkpoints (Save/Load position)
        if (p.bind_save_pos) output += `bind "${p.bind_save_pos}" "writeip; con_logfile cfg/position.log; getpos; con_logfile ."\n`
        if (p.bind_load_pos) output += `bind "${p.bind_load_pos}" "exec position.log"\n`

        output += '\n'
    }

    output += '\nhost_writeconfig\n'
    return output
}
